import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/joy';

interface SplitViewProps {
  left: React.ReactNode;
  right: React.ReactNode;
  defaultSplit?: number;
  minSize?: number;
  onSplitChange?: (split: number) => void;
}

export const SplitView: React.FC<SplitViewProps> = ({
  left,
  right,
  defaultSplit = 50,
  minSize = 20,
  onSplitChange,
}) => {
  const [split, setSplit] = useState(defaultSplit);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const newSplit = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    if (newSplit >= minSize && newSplit <= 100 - minSize) {
      setSplit(newSplit);
      onSplitChange?.(newSplit);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          width: `${split}%`,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {left}
      </Box>

      {/* Splitter */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 4,
          height: '100%',
          bgcolor: isDragging ? 'primary.500' : 'divider',
          cursor: 'col-resize',
          position: 'relative',
          transition: isDragging ? 'none' : 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'primary.300',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 4,
            height: 40,
            bgcolor: 'background.surface',
            borderRadius: 'sm',
            opacity: isDragging ? 1 : 0,
            transition: 'opacity 0.2s',
          },
          '&:hover::before': {
            opacity: 0.5,
          },
        }}
      />

      {/* Right Panel */}
      <Box
        sx={{
          width: `${100 - split}%`,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {right}
      </Box>
    </Box>
  );
};
